import { PhoneIcon, PhoneIcon2 } from "@/assets";
import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPSlot } from "@/components/ui/input-otp";
import { Separator } from "@/components/ui/separator";
import { useAuthState } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import { AddTwoFA, getTwoFa } from "@/services/Settings.service";
import { Storage } from "@/services/storage-services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Copy, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

const SetUpTwoFA = () => {
  const [step, setStep] = useState(1);
  const { state, logout } = useAuthState();
  const user = Storage.getItem("user");
  const { data, isFetching } = useQuery({
    queryKey: ["twofa"],
    queryFn: () => getTwoFa(),
    enabled: !user?.twoFactorAuthentication
  });

  const [open, setOpen] = useState(false);
  const copyToClipboard = async (value) => { };

  console.log(user);
  return (
    <CustomModal
      onOpenChange={setOpen}
      open={open}
      button={
        user?.twoFactorAuthentication ? (
          <div className="flex h-10 lg:h-20 bg-off-white-1 rounded-[12px] py-4 px-3 lg:px-5 lg:gap-6 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full lg:w-12 lg:h-12 bg-off-white-4">
                <PhoneIcon />
              </div>
              <span className="text-xs lg:text-sm text-gray">
                Google Authenticator
              </span>
            </div>
            <Button variant="ghost" onClick={() => setStep(2)}>
              Disable <ChevronRight />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-40 bg-transparent text-primary0 border-primary0 hover:text-primary0"
            onClick={() => setStep(1)}
          >
            Click to Setup <ChevronRight />
          </Button>
        )
      }
      size="lg"
    >
      <div className="flex flex-col gap-8">
        <DialogHeader className="flex flex-row  w-full p-0 items-center gap-4 bg-off-white-1 py-6 px-8 rounded-t-[16px]">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-off-white-3">
            <PhoneIcon2 />
          </div>
          <div className="flex flex-col ">
            <DialogTitle className="text-sm font-normal capitalize text-primary-3">
              Setup Authentication App
            </DialogTitle>
            <DialogDescription className="text-sm">
              Add auth app by completing the instructions below
            </DialogDescription>
          </div>
        </DialogHeader>
      </div>
      <div className="grid gap-4 px-10 pb-5">
        <span>Step {step}</span>
        <p className="flex items-center justify-center text-sm text-gray bg-green0 px-4 py-[10px] rounded-[8px]">
          {step === 1
            ? "Scan the QR Code below or copy and paste the manual authentication key into your authentication app"
            : "Enter the code generated from your authentication app to complete setup."}
        </p>

        {step === 1 && (
          <div className="grid items-center  gap-6 p-6 border-off-white border-[1px] rounded-[8px]">
            <div className="flex justify-center">
              <img src={data?.data?.data?.qr} className="w-[136px] h-[136px]" />
            </div>
            <div className="flex items-center justify-start gap-3">
              <div className="w-full">
                <Separator />
              </div>
              <span className="text-xs text-gray-80">Or</span>
              <div className="w-full">
                <Separator />
              </div>
            </div>
            <div className="flex gap-3">
              <Input value={data?.data?.data?.secret} disabled />
              <Button
                className="bg-primary-5 text-gray-4"
                onClick={() => copyToClipboard(data?.data?.data?.secret)}
              >
                Copy
                <Copy />
              </Button>
            </div>
          </div>
        )}
      </div>

      {step === 1 && (
        <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
          <>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
              >
                <ChevronLeft /> Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
              onClick={() => setStep(2)}
            >
              Proceed <ChevronRight />
            </Button>
          </>
        </DialogFooter>
      )}
      {step === 2 && (
        <AddTwoFAComp
          setStep={setStep}
          close={setOpen}
          check={user?.twoFactorAuthentication}
        />
      )}
    </CustomModal>
  );
};

export default SetUpTwoFA;

const AddTwoFAComp = ({ setStep, close, check }) => {
  const q = useQueryClient();
  const { logout } = useAuthState();
  const form = useForm();
  const mutation = useMutation({
    mutationFn: (formData) => AddTwoFA(formData),
    mutationKey: ["addTwoFA"],
    onSuccess: () => {
      close(false);
      q.clear();
      setStep(1);

      ToastMessage(type.SUCCESS, "Settings saved successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const onSubmit = async (data) => {
    let c;
    if (check) {
      c = {
        status: false,
        twoFactorAuthenticationCode: data?.otp,
      };
    } else {
      c = {
        status: true,
        twoFactorAuthenticationCode: data?.otp,
      };
    }
    await mutation.mutate(c);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-8">
          <FormField
            control={form.control}
            name="otp"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <div className="w-[350px] mx-auto pb-6">
                      <InputOTP maxLength={6} {...field} className="flex ">
                        <InputOTPSlot index={0} className="w-12 h-14" />
                        <InputOTPSlot index={1} className="w-12 h-14" />
                        <InputOTPSlot index={2} className="w-12 h-14" />
                        <InputOTPSlot index={3} className="w-12 h-14" />
                        <InputOTPSlot index={4} className="w-12 h-14" />
                        <InputOTPSlot index={5} className="w-12 h-14" />
                      </InputOTP>
                    </div>
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </div>

        <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
          <Button
            type="button"
            variant="ghost"
            className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
          >
            <ChevronLeft /> Step 1
          </Button>
          <Button
            type="submit"
            className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
            disabled={
              !form.formState.isDirty ||
              !form.formState.isValid ||
              form.getValues("otp") == "" ||
              form.formState.isSubmitting ||
              mutation.isPending
            }
          >
            {mutation.isPending && (
              <Loader2 className="text-white animate-spin" />
            )}
            Submit <ChevronRight />
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
